import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Plus, X, GripVertical } from 'lucide-react';
import api from '../api/api';
import { Confirm, Alert, Message, ErrorState, PageSkeleton, SearchableSelect, Modal } from '../components';
import { PageHeader, Toolbar } from '../components/layout';
import { getApiErrorMessage } from '../utils/apiError';
import { getConfiguredStatusName } from '../utils/statusOptions';

const RulesPage = ({ canWriteGroups = false }) => {
  const navigate = useNavigate();
  const groupDragId = useRef(null);
  const [activeGroupDrag, setActiveGroupDrag] = useState(null);
  const groupMotionNodes = useRef(new Map());
  const groupMotionPositions = useRef(new Map());
  const groupAnimations = useRef(new Map());
  const groupOrderSaving = useRef(false);
  const [groupOrderBusy, setGroupOrderBusy] = useState(false);
  const [groupOrderMessage, setGroupOrderMessage] = useState(null);
  const [groupDropTarget, setGroupDropTarget] = useState(null);
  const [groupSaving, setGroupSaving] = useState(false);
  const [rules, setRules] = useState([]);
  const [groups, setGroups] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [fields, setFields] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draggingItem, setDraggingItem] = useState(null);
  const [draggingGroupId, setDraggingGroupId] = useState(null);
  const [ruleFilters, setRuleFilters] = useState({
    keyword: '',
    group_id: '',
    rule_status: '',
    delay_status: ''
  });
  const [expandedGroupIds, setExpandedGroupIds] = useState([]);

  // 规则组管理弹窗状态
  const [showGroupListModal, setShowGroupListModal] = useState(false);
  const [showGroupEditorModal, setShowGroupEditorModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    is_active: true,
    stop_on_match: true,
    webhook_url: ''
  });
  const [delayPolicyForm, setDelayPolicyForm] = useState({
    enabled: false,
    calendar_id: '',
    condition_field: 'priority',
    condition_operator: 'not_in',
    condition_value_text: 'Highest, High',
    delay_mode: 'next_business_start'
  });

  // 弹窗状态
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rulesRes, groupsRes, calendarsRes, fieldsRes, statusesRes] = await Promise.all([
        api.getRules(),
        api.getGroups(),
        api.getCalendars(),
        api.getFields(),
        api.getStatuses()
      ]);

      const rulesData = Array.isArray(rulesRes.data) ? rulesRes.data :
                       (rulesRes.data && Array.isArray(rulesRes.data.rules)) ? rulesRes.data.rules : [];
      const groupsData = Array.isArray(groupsRes.data) ? groupsRes.data :
                        (groupsRes.data && Array.isArray(groupsRes.data.groups)) ? groupsRes.data.groups : [];

      // 按规则组ID和优先级排序
      const sortedRules = rulesData.sort((a, b) => {
        if (a.group_id !== b.group_id) {
          return a.group_id - b.group_id;
        }
        return a.priority - b.priority;
      });

      setRules(sortedRules);
      setGroups([...groupsData].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0) || a.id - b.id));
      setCalendars(Array.isArray(calendarsRes.data) ? calendarsRes.data : []);
      setFields(Array.isArray(fieldsRes.data) ? fieldsRes.data : ((fieldsRes.data && Array.isArray(fieldsRes.data.fields)) ? fieldsRes.data.fields : []));
      setStatuses(Array.isArray(statusesRes.data) ? statusesRes.data : []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const normalizeText = (value) => String(value || '').toLowerCase();

  const getGroupById = (groupId) => groups.find(group => String(group.id) === String(groupId));

  const isGroupExpandedById = (groupId) => expandedGroupIds.includes(String(groupId));

  const toggleGroupExpanded = (groupId) => {
    const normalizedGroupId = String(groupId);
    setExpandedGroupIds(prev => (
      prev.includes(normalizedGroupId)
        ? prev.filter(id => id !== normalizedGroupId)
        : [...prev, normalizedGroupId]
    ));
  };

  const handleAddRuleToGroup = (groupId) => {
    navigate(`/rules/new?group_id=${encodeURIComponent(groupId)}`);
  };

  const getDelayPolicyLabel = (group) => {
    if (!group?.delay_enabled) return '未开启延迟';
    const policy = group.delay_policy || {};
    const calendar = calendars.find(item => String(item.id) === String(policy.calendar_id || group.delay_calendar_id));
    const calendarName = calendar?.name || (policy.calendar_id ? `日历 #${policy.calendar_id}` : '未选日历');
    return `非工作时间延迟 · ${calendarName}`;
  };

  const getActionTags = (rule) => {
    const actionLabels = {
      assign: '分配工单',
      update_priority: '更新优先级',
      transition: '状态转换',
      set_field: '设置字段',
      ai_set_field: 'AI 设置字段',
      add_comment: '添加评论',
      send_https_request: '发送 HTTPS 请求',
      noop: '不执行操作'
    };

    if (Array.isArray(rule.action_items) && rule.action_items.length > 0) {
      return rule.action_items.map(action => {
        const actionType = action.type || action.action_type;
        const label = actionLabels[actionType] || actionType || '未知动作';
        if (actionType === 'assign') {
          const count = String(action.target || '').split(',').filter(Boolean).length;
          return count > 0 ? `${label} · ${count} 人` : label;
        }
        if (actionType === 'transition') {
          const statusName = getConfiguredStatusName(statuses, action.target);
          return statusName ? `${label} · ${statusName}` : label;
        }
        if (actionType === 'set_field' || actionType === 'ai_set_field') {
          const field = fields.find(item => item.field_id === action.field);
          return field?.display_name ? `${label} · ${field.display_name}` : label;
        }
        if (actionType === 'send_https_request') {
          return action.url ? `${label} · ${(action.method || 'POST').toUpperCase()}` : label;
        }
        return label;
      });
    }

    try {
      const params = rule.action_params ? JSON.parse(rule.action_params) : null;
      if (params?.actions && Array.isArray(params.actions) && params.actions.length > 0) {
        return params.actions.map(action => {
          const label = actionLabels[action.type] || action.type || '未知动作';
          if (action.type === 'assign') {
            const count = String(action.target || '').split(',').filter(Boolean).length;
            return count > 0 ? `${label} · ${count} 人` : label;
          }
          if (action.type === 'transition') {
            const statusName = getConfiguredStatusName(statuses, action.target);
            return statusName ? `${label} · ${statusName}` : label;
          }
          if (action.type === 'set_field' || action.type === 'ai_set_field') {
            const field = fields.find(item => item.field_id === action.field);
            return field?.display_name ? `${label} · ${field.display_name}` : label;
          }
          if (action.type === 'send_https_request') {
            return action.url ? `${label} · ${(action.method || 'POST').toUpperCase()}` : label;
          }
          return label;
        });
      }
    } catch (e) {}

    const legacyLabel = actionLabels[rule.action_type] || rule.action_type || '未知动作';
    return [legacyLabel];
  };

  const getRuleSearchText = (rule) => {
    const group = getGroupById(rule.group_id);
    return [
      rule.name,
      rule.description,
      rule.creator_name,
      rule.conditions,
      rule.match_field,
      rule.match_operator,
      rule.match_value,
      getActionTags(rule).join(' '),
      group?.name,
      group?.description
    ].map(normalizeText).join(' ');
  };

  const getGroupSearchText = (group) => [
    group?.name,
    group?.description,
    group?.webhook_url,
    getDelayPolicyLabel(group),
    group?.is_active === false ? '已禁用' : '已启用',
    group?.stop_on_match === false ? '继续匹配' : '匹配后停止'
  ].map(normalizeText).join(' ');

  const filteredRules = rules.filter(rule => {
    const group = getGroupById(rule.group_id);
    const keyword = normalizeText(ruleFilters.keyword).trim();
    if (keyword && !getRuleSearchText(rule).includes(keyword)) return false;
    if (ruleFilters.group_id && String(rule.group_id) !== String(ruleFilters.group_id)) return false;
    if (ruleFilters.rule_status === 'active' && !rule.is_active) return false;
    if (ruleFilters.rule_status === 'inactive' && rule.is_active) return false;
    if (ruleFilters.delay_status === 'enabled' && !group?.delay_enabled) return false;
    if (ruleFilters.delay_status === 'disabled' && group?.delay_enabled) return false;
    return true;
  });

  // 按规则组分组
  const getRulesByGroup = () => {
    const grouped = {};
    const keyword = normalizeText(ruleFilters.keyword).trim();
    groups
      .filter(group => {
        if (ruleFilters.group_id && String(group.id) !== String(ruleFilters.group_id)) return false;
        if (
          ruleFilters.delay_status &&
          (ruleFilters.delay_status === 'enabled' ? !group.delay_enabled : group.delay_enabled)
        ) return false;
        if (keyword) {
          const groupRules = filteredRules.filter(rule => rule.group_id === group.id);
          if (groupRules.length === 0 && !getGroupSearchText(group).includes(keyword)) return false;
        }
        return true;
      })
      .forEach(group => {
      grouped[group.id] = {
        group,
        rules: filteredRules.filter(rule => rule.group_id === group.id)
      };
    });
    // 包含未分组的规则
    const ungrouped = filteredRules.filter(rule => !rule.group_id || !groups.find(g => g.id === rule.group_id));
    if (ungrouped.length > 0) {
      grouped['ungrouped'] = {
        group: { id: 'ungrouped', name: '未分组', description: '未分配到任何规则组的规则' },
        rules: ungrouped
      };
    }
    return grouped;
  };

  const toggleRuleStatus = async (ruleId, currentStatus) => {
    try {
      const response = await api.updateRuleStatus(ruleId);
      const newStatus = response.data.is_active;
      setRules(rules.map(rule =>
        rule.id === ruleId ? { ...rule, is_active: newStatus } : rule
      ));
    } catch (err) {
      setAlertState({
        isOpen: true,
        message: '更新规则状态失败: ' + getApiErrorMessage(err, '未知错误')
      });
    }
  };

  const deleteRule = async (ruleId) => {
    setConfirmState({
      isOpen: true,
      message: '确定要删除这条规则吗？',
      onConfirm: async () => {
        try {
          await api.deleteRule(ruleId);
          setRules(rules.filter(rule => rule.id !== ruleId));
        } catch (err) {
          setAlertState({
            isOpen: true,
            message: '删除规则失败: ' + getApiErrorMessage(err, '未知错误')
          });
        }
        setConfirmState({ ...confirmState, isOpen: false });
      }
    });
  };

  // 规则组管理
  const defaultDelayPolicyForm = {
    enabled: false,
    calendar_id: '',
    condition_field: 'priority',
    condition_operator: 'not_in',
    condition_value_text: 'Highest, High',
    delay_mode: 'next_business_start'
  };

  const normalizeDelayPolicyForForm = (policy) => {
    const value = policy?.condition_value;
    return {
      enabled: policy?.enabled === true,
      calendar_id: policy?.calendar_id ? String(policy.calendar_id) : '',
      condition_field: policy?.condition_field || 'priority',
      condition_operator: policy?.condition_operator || 'not_in',
      condition_value_text: Array.isArray(value) ? value.join(', ') : (value || 'Highest, High'),
      delay_mode: policy?.delay_mode || 'next_business_start'
    };
  };

  const buildDelayPolicyPayload = () => ({
    enabled: delayPolicyForm.enabled,
    calendar_id: delayPolicyForm.calendar_id ? Number(delayPolicyForm.calendar_id) : null,
    condition_field: delayPolicyForm.condition_field,
    condition_operator: delayPolicyForm.condition_operator,
    condition_value: delayPolicyForm.condition_value_text
      .split(',')
      .map(item => item.trim())
      .filter(Boolean),
    delay_mode: delayPolicyForm.delay_mode
  });

  const openGroupEditorModal = async (group = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({
        name: group.name,
        description: group.description || '',
        is_active: group.is_active !== false,
        stop_on_match: group.stop_on_match !== false,
        webhook_url: group.webhook_url || ''
      });
      try {
        const policyRes = await api.getGroupDelayPolicy(group.id);
        setDelayPolicyForm(normalizeDelayPolicyForForm(policyRes.data));
      } catch (err) {
        setDelayPolicyForm(defaultDelayPolicyForm);
      }
    } else {
      setEditingGroup(null);
      setGroupForm({
        name: '',
        description: '',
        is_active: true,
        stop_on_match: true,
        webhook_url: ''
      });
      setDelayPolicyForm(defaultDelayPolicyForm);
    }
    setShowGroupEditorModal(true);
  };

  const closeGroupEditorModal = () => {
    setShowGroupEditorModal(false);
    setEditingGroup(null);
    setGroupForm({ name: '', description: '', is_active: true, stop_on_match: true, webhook_url: '' });
    setDelayPolicyForm(defaultDelayPolicyForm);
  };

  const closeGroupListModal = () => {
    setShowGroupListModal(false);
  };

  const saveGroup = async () => {
    if (groupSaving) return;
    if (!groupForm.name.trim()) {
      setAlertState({ isOpen: true, message: '请输入规则组名称' });
      return;
    }
    if (delayPolicyForm.enabled && !delayPolicyForm.calendar_id) {
      setAlertState({ isOpen: true, message: '启用延迟触发时请选择工作日历' });
      return;
    }
    if (delayPolicyForm.enabled && !delayPolicyForm.condition_field.trim()) {
      setAlertState({ isOpen: true, message: '启用延迟触发时请输入判断字段' });
      return;
    }
    if (delayPolicyForm.enabled && delayConditionNeedsValue && !delayPolicyForm.condition_value_text.trim()) {
      setAlertState({ isOpen: true, message: '启用延迟触发时请输入判断值' });
      return;
    }

    setGroupSaving(true);
    try {
      if (editingGroup) {
        await api.updateGroup(editingGroup.id, groupForm);
        await api.updateGroupDelayPolicy(editingGroup.id, buildDelayPolicyPayload());
      } else {
        const response = await api.createGroup(groupForm);
        await api.updateGroupDelayPolicy(response.data.id, buildDelayPolicyPayload());
      }
      closeGroupEditorModal();
      closeGroupListModal();
      fetchData();
    } catch (err) {
      setAlertState({ isOpen: true, message: '保存规则组失败: ' + err.message });
    } finally {
      setGroupSaving(false);
    }
  };

  const toggleGroupStatus = async (group) => {
    try {
      const newStatus = group.is_active === false ? true : false;
      await api.updateGroup(group.id, { ...group, is_active: newStatus });
      setGroups(groups.map(g => g.id === group.id ? { ...g, is_active: newStatus } : g));
    } catch (err) {
      setAlertState({ isOpen: true, message: '更新规则组状态失败: ' + err.message });
    }
  };

  const deleteGroup = async (groupId) => {
    const group = groups.find(g => g.id === groupId);
    const groupRules = rules.filter(r => r.group_id === groupId);

    if (groupRules.length > 0) {
      setAlertState({
        isOpen: true,
        message: `规则组 "${group.name}" 中还有 ${groupRules.length} 条规则，请先移动或删除这些规则。`
      });
      return;
    }

    setConfirmState({
      isOpen: true,
      message: `确定要删除规则组 "${group.name}" 吗？`,
      onConfirm: async () => {
        try {
          await api.deleteGroup(groupId);
          fetchData();
        } catch (err) {
          setAlertState({ isOpen: true, message: '删除规则组失败: ' + (err.response?.data?.detail || err.message) });
        }
        setConfirmState({ ...confirmState, isOpen: false });
      }
    });
  };

  const captureGroupPositions = () => {
    groupAnimations.current.forEach(animation => animation.cancel());
    groupAnimations.current.clear();
    groupMotionPositions.current = new Map(
      [...groupMotionNodes.current].map(([key, node]) => [key, node.getBoundingClientRect().top])
    );
  };
  useLayoutEffect(() => {
    const positions = groupMotionPositions.current;
    groupMotionPositions.current = new Map();
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    groupMotionNodes.current.forEach((node, key) => {
      if (!positions.has(key)) return;
      const offset = positions.get(key) - node.getBoundingClientRect().top;
      if (!offset || !node.animate) return;
      const animation = node.animate([
        { transform: `translateY(${offset}px)` },
        { transform: 'translateY(0)' }
      ], { duration: 240, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
      groupAnimations.current.set(key, animation);
      animation.onfinish = () => {
        if (groupAnimations.current.get(key) === animation) groupAnimations.current.delete(key);
      };
    });
  }, [groups]);
  useEffect(() => () => {
    groupAnimations.current.forEach(animation => animation.cancel());
  }, []);
  const groupMotionRef = key => node => {
    if (node) groupMotionNodes.current.set(key, node);
    else groupMotionNodes.current.delete(key);
  };
  const groupMotionClass = id => {
    const source = groups.findIndex(group => group.id === activeGroupDrag);
    const target = groups.findIndex(group => group.id === id);
    return [
      'rule-group-sortable',
      activeGroupDrag === id ? 'is-group-dragging' : '',
      groupDropTarget === id && activeGroupDrag !== id
        ? `is-group-drop-target ${source < target ? 'is-drop-after' : 'is-drop-before'}` : ''
    ].filter(Boolean).join(' ');
  };

  const handleGroupDragStart = (event, id) => {
    event.stopPropagation();
    if (!canWriteGroups || groupOrderSaving.current) { event.preventDefault(); return; }
    groupDragId.current = id;
    setActiveGroupDrag(id);
    const row = event.currentTarget.closest('.rule-group-sortable');
    const preview = row?.querySelector('.rule-group-list-header') || row;
    if (preview && event.dataTransfer.setDragImage) {
      event.dataTransfer.setDragImage(preview, 24, 24);
    }
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(id));
  };
  const handleGroupDragEnd = () => {
    groupDragId.current = null;
    setActiveGroupDrag(null);
    setGroupDropTarget(null);
  };
  const handleGroupDragOver = (event, id) => {
    if (groupDragId.current == null || id === 'ungrouped' || groupOrderSaving.current) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setGroupDropTarget(id);
  };
  const handleGroupDrop = async (event, id) => {
    if (groupDragId.current == null || id === 'ungrouped') return;
    event.preventDefault();
    event.stopPropagation();
    const sourceId = groupDragId.current;
    handleGroupDragEnd();
    if (!canWriteGroups || groupOrderSaving.current || sourceId === id) return;
    const from = groups.findIndex(group => group.id === sourceId);
    const to = groups.findIndex(group => group.id === id);
    if (from < 0 || to < 0) return;
    const previous = groups;
    const next = [...groups];
    next.splice(to, 0, next.splice(from, 1)[0]);
    const ordered = next.map((group, priority) => ({ ...group, priority }));
    groupOrderSaving.current = true;
    setGroupOrderBusy(true);
    setGroupOrderMessage(null);
    captureGroupPositions();
    setGroups(ordered);
    try {
      await api.reorderGroups(ordered.map(({ id, priority }) => ({ id, priority })));
      setGroupOrderMessage({ type: 'success', text: '规则组顺序已保存' });
    } catch (error) {
      captureGroupPositions();
      setGroups(previous);
      setGroupOrderMessage({ type: 'error', text: '规则组排序保存失败：' + getApiErrorMessage(error, '请重试') });
    } finally {
      groupOrderSaving.current = false;
      setGroupOrderBusy(false);
    }
  };
  const groupDragHandle = (group) => canWriteGroups && group.id !== 'ungrouped' && (
    <button type="button" className="rule-group-drag-handle" draggable={!groupOrderBusy}
      disabled={groupOrderBusy} aria-label={`拖拽排序规则组：${group.name}`} title="拖拽调整规则组执行顺序"
      onClick={event => event.stopPropagation()}
      onDragStart={event => handleGroupDragStart(event, group.id)} onDragEnd={handleGroupDragEnd}>
      <GripVertical size={18} aria-hidden="true" />
    </button>
  );

  // 拖拽开始
  const handleDragStart = (e, rule, groupId) => {
    setDraggingItem(rule);
    setDraggingGroupId(groupId);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  // 拖拽结束
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggingItem(null);
    setDraggingGroupId(null);
  };

  // 拖拽经过
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // 放置
  const handleDrop = async (e, targetRule, targetGroupId) => {
    e.preventDefault();

    if (!draggingItem || draggingItem.id === targetRule.id) return;
    if (draggingGroupId !== targetGroupId) {
      setAlertState({ isOpen: true, message: '只能在同一规则组内调整顺序' });
      return;
    }

    const groupedRules = getRulesByGroup();
    const groupRules = groupedRules[targetGroupId]?.rules || [];

    const dragIndex = groupRules.findIndex(r => r.id === draggingItem.id);
    const targetIndex = groupRules.findIndex(r => r.id === targetRule.id);

    if (dragIndex === -1 || targetIndex === -1) return;

    const newGroupRules = [...groupRules];
    newGroupRules.splice(dragIndex, 1);
    newGroupRules.splice(targetIndex, 0, draggingItem);

    const updatedRules = newGroupRules.map((rule, index) => ({
      ...rule,
      priority: index
    }));

    const actualGroupId = draggingItem.group_id;
    const otherRules = rules.filter(r => r.group_id !== actualGroupId);
    setRules([...otherRules, ...updatedRules]);

    try {
      for (const rule of updatedRules) {
        await api.updateRule(rule.id, { priority: rule.priority });
      }
    } catch (err) {
      console.error('更新优先级失败:', err);
      setAlertState({ isOpen: true, message: '保存排序失败，请刷新页面重试' });
      fetchData();
    }
  };

  const getConditionsText = (rule) => {
    if (rule.conditions && rule.conditions !== '[]') {
      try {
        const conditions = JSON.parse(rule.conditions);
        if (Array.isArray(conditions) && conditions.length > 0) {
          return `${conditions.length} 个条件 (${rule.condition_logic === 'or' ? '任一' : '全部'})`;
        }
      } catch (e) {}
    }
    if (rule.match_field && rule.match_value) {
      return `${rule.match_field} ${rule.match_operator || 'contains'} ${rule.match_value}`;
    }
    return '无条件';
  };

  const delayOperatorOptions = [
    { value: 'contains', label: '包含' },
    { value: 'not_contains', label: '不包含' },
    { value: 'equals', label: '等于' },
    { value: 'not_equals', label: '不等于' },
    { value: 'in', label: '属于' },
    { value: 'not_in', label: '不属于' },
    { value: 'is_empty', label: '为空' },
    { value: 'is_not_empty', label: '不为空' }
  ];

  const delayFieldOptions = [
    { value: 'summary', label: '摘要' },
    { value: 'description', label: '描述' },
    { value: 'priority', label: '优先级' },
    { value: 'status', label: '状态' },
    { value: 'issuetype', label: '工单类型' },
    ...fields.map(field => ({ value: field.field_id, label: field.display_name || field.field_id }))
  ].filter((option, index, options) => (
    options.findIndex(item => item.value === option.value) === index
  ));

  const delayConditionNeedsValue = !['is_empty', 'is_not_empty'].includes(delayPolicyForm.condition_operator);

  const groupedData = getRulesByGroup();
  const activeRules = rules.filter(rule => rule.is_active).length;
  const disabledGroups = groups.filter(group => group.is_active === false).length;

  if (error) {
    return <div className="rules-console-page"><PageHeader title="规则管理" /><ErrorState title="无法加载规则管理" description={error} onRetry={fetchData} /></div>;
  }

  return (
    <div className="rules-console-page">
      <Message message={groupOrderMessage?.text || ''} type={groupOrderMessage?.type || 'info'} />
      <PageHeader
        title="规则管理"
        actions={(
          <>
          <button
            className="btn btn-secondary"
            onClick={() => setShowGroupListModal(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            管理规则组
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/rules/new')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            新建规则
          </button>
          </>
        )}
      />

      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">规则组</div>
          <div className="metric-value">{groups.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">规则总数</div>
          <div className="metric-value">{rules.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">启用规则</div>
          <div className="metric-value">{activeRules}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">禁用规则组</div>
          <div className="metric-value">{disabledGroups}</div>
        </div>
      </div>

      <Toolbar className="page-toolbar">
        <div className="toolbar-fields">
          <div className="form-group" style={{ flex: '1 1 260px', marginBottom: 0 }}>
            <label className="form-label">搜索规则或规则组</label>
            <input
              type="text"
              className="form-input"
              value={ruleFilters.keyword}
              onChange={(e) => setRuleFilters(prev => ({ ...prev, keyword: e.target.value }))}
              placeholder="输入规则名、规则组、条件、动作或创建人"
            />
          </div>
          <div className="form-group" style={{ width: '190px', marginBottom: 0 }}>
            <label className="form-label">规则组</label>
            <SearchableSelect
              value={ruleFilters.group_id}
              options={[
                { value: '', label: '全部规则组' },
                ...groups.map(group => ({ value: group.id, label: group.name }))
              ]}
              onChange={(value) => setRuleFilters(prev => ({ ...prev, group_id: value }))}
              placeholder="全部规则组"
            />
          </div>
          <div className="form-group" style={{ width: '170px', marginBottom: 0 }}>
            <label className="form-label">规则状态</label>
            <SearchableSelect
              value={ruleFilters.rule_status}
              options={[
                { value: '', label: '全部规则状态' },
                { value: 'active', label: '启用规则' },
                { value: 'inactive', label: '禁用规则' }
              ]}
              onChange={(value) => setRuleFilters(prev => ({ ...prev, rule_status: value }))}
              placeholder="全部规则状态"
              searchable={false}
            />
          </div>
          <div className="form-group" style={{ width: '170px', marginBottom: 0 }}>
            <label className="form-label">延迟状态</label>
            <SearchableSelect
              value={ruleFilters.delay_status}
              options={[
                { value: '', label: '全部延迟状态' },
                { value: 'enabled', label: '已开启延迟' },
                { value: 'disabled', label: '未开启延迟' }
              ]}
              onChange={(value) => setRuleFilters(prev => ({ ...prev, delay_status: value }))}
              placeholder="全部延迟状态"
              searchable={false}
            />
          </div>
        </div>
        <div className="toolbar-actions">
          <span className="badge badge-default">{filteredRules.length} / {rules.length} 条规则</span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setRuleFilters({ keyword: '', group_id: '', rule_status: '', delay_status: '' })}
          >
            清空筛选
          </button>
        </div>
      </Toolbar>

      {loading ? (
        <div className="rules-console-skeleton">
          <PageSkeleton metricCount={4} rowCount={6} columnCount={5} label="加载规则中" />
        </div>
      ) : (
        <div className="rules-console-list">
          {[...groups.map(group => String(group.id)), 'ungrouped'].filter(id => groupedData[id]).map(id => [id, groupedData[id]]).map(([groupId, { group, rules: groupRules }]) => {
            const isGroupExpanded = isGroupExpandedById(groupId);
            const canAddRuleToGroup = group.id !== 'ungrouped';

            return (
            <section key={groupId} ref={groupMotionRef(`list:${group.id}`)} className={`rule-group-list-section ${groupMotionClass(group.id)}`}
              onDragOver={event => handleGroupDragOver(event, group.id)}
              onDrop={event => handleGroupDrop(event, group.id)}>
              <header
                className="rule-group-list-header"
                onClick={() => toggleGroupExpanded(groupId)}
              >
                <div className="rule-group-heading">
                  {groupDragHandle(group)}
                  <button
                    type="button"
                    className="rule-group-expand-button"
                    aria-expanded={isGroupExpanded}
                    title={isGroupExpanded ? '收起规则' : '展开规则'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <div className="rule-group-title-block">
                    <h3 className="rule-group-title">
                      {group.name}
                      {group.is_active === false && (
                        <span className="rule-group-status-tag">已禁用</span>
                      )}
                    </h3>
                    <div className="rule-group-tags">
                      {group.is_active === false && (
                        <span className="rule-group-tag is-muted">已禁用</span>
                      )}
                      <span className="rule-group-tag">
                        {group.stop_on_match === false ? '继续匹配' : '匹配后停止'}
                      </span>
                      <span className={`rule-group-tag ${group.delay_enabled ? 'is-warning' : 'is-muted'}`}>
                        {getDelayPolicyLabel(group)}
                      </span>
                      {group.webhook_url && <span className="rule-group-tag is-muted">Webhook</span>}
                    </div>
                  </div>
                </div>
                <div
                  className="rule-group-header-actions"
                  onClick={(event) => event.stopPropagation()}
                >
                  {canAddRuleToGroup && (
                    <>
                      <button
                        type="button"
                        className="rule-group-action-icon-button"
                        onClick={() => openGroupEditorModal(group)}
                        aria-label="编辑规则组"
                        title="编辑规则组"
                      >
                        <Pencil size={18} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="rule-group-action-icon-button is-danger"
                        onClick={() => deleteGroup(group.id)}
                        aria-label="删除规则组"
                        title="删除规则组"
                      >
                        <X size={18} aria-hidden="true" />
                      </button>
                    </>
                  )}
                  {canAddRuleToGroup && (
                    <button
                      type="button"
                      className="rule-group-action-icon-button is-primary"
                      onClick={() => handleAddRuleToGroup(group.id)}
                      aria-label="添加规则"
                      title="添加规则"
                    >
                      <Plus size={18} aria-hidden="true" />
                    </button>
                  )}
                  <label className="switch" title={group.is_active !== false ? '禁用规则组' : '启用规则组'}>
                    <input
                      type="checkbox"
                      checked={group.is_active !== false}
                      onChange={() => toggleGroupStatus(group)}
                    />
                    <span className="switch-slider"></span>
                  </label>
                  <span className="badge badge-default">{groupRules.length} 条规则</span>
                </div>
              </header>

              <div
                className={`rule-group-expand-shell ${isGroupExpanded ? 'is-expanded' : ''}`}
                aria-hidden={!isGroupExpanded}
              >
                <div className="rule-group-expand-inner">
                <div className="rule-group-list-body">
                  {groupRules.length === 0 ? (
                  <div className="rule-group-empty-state">
                    <p>该规则组暂无规则</p>
                    {canAddRuleToGroup && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleAddRuleToGroup(group.id)}
                        title="添加规则到规则组"
                      >
                        添加规则
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="rule-list-rows">
                    {groupRules.map(rule => (
                      <article
                        className="rule-list-row"
                        key={rule.id}
                        onClick={() => navigate(`/rules/edit/${rule.id}`)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, rule, groupId)}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, rule, groupId)}
                      >
                        <span
                          className="rule-list-drag-handle"
                          title="拖拽调整顺序"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <circle cx="9" cy="5" r="1.5"/>
                            <circle cx="15" cy="5" r="1.5"/>
                            <circle cx="9" cy="12" r="1.5"/>
                            <circle cx="15" cy="12" r="1.5"/>
                            <circle cx="9" cy="19" r="1.5"/>
                            <circle cx="15" cy="19" r="1.5"/>
                          </svg>
                        </span>
                        <div className="rule-list-identity">
                          <strong>{rule.name}</strong>
                          {(rule.description || rule.creator_name) && (
                            <span>{rule.description || `由 ${rule.creator_name} 创建`}</span>
                          )}
                        </div>
                        <div className="rule-list-condition">
                          <strong>{getConditionsText(rule)}</strong>
                          <span>匹配条件</span>
                        </div>
                        <div className="rule-list-actions-summary">
                          {getActionTags(rule).map((actionLabel, actionIndex) => (
                            <span key={`${rule.id}-${actionIndex}`} className="badge badge-info">
                              {actionLabel}
                            </span>
                          ))}
                        </div>
                        <div
                          className="rule-list-controls"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <label className="switch" title={rule.is_active ? '禁用规则' : '启用规则'}>
                            <input
                              type="checkbox"
                              checked={rule.is_active}
                              onChange={() => toggleRuleStatus(rule.id, rule.is_active)}
                            />
                            <span className="switch-slider"></span>
                          </label>
                          <button
                            type="button"
                            className="rule-list-icon-button is-danger"
                            onClick={() => deleteRule(rule.id)}
                            aria-label="删除规则"
                            title="删除规则"
                          >
                            <X size={20} aria-hidden="true" />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
                </div>
                </div>
              </div>
            </section>
          );
          })}

          {rules.length === 0 && (
            <div className="configuration-list-surface">
              <div className="empty-state">
                <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <p>暂无规则</p>
                <button className="btn btn-primary btn-sm" onClick={() => navigate('/rules/new')}>
                  创建第一条规则
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 规则组管理弹窗 */}
      {showGroupListModal && !showGroupEditorModal && (
        <Modal isOpen title={<>管理规则组</>} onClose={closeGroupListModal} busy={groupSaving || confirmState.isOpen} size="large" className="admin-standard-dialog">


            <div className="modal-body">
              <div>
                {groups.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>暂无规则组</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {groups.map(group => (
                      <div
                        key={group.id}
                        ref={groupMotionRef(`modal:${group.id}`)}
                        className={groupMotionClass(group.id)}
                        onDragOver={event => handleGroupDragOver(event, group.id)}
                        onDrop={event => handleGroupDrop(event, group.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px',
                          background: 'var(--bg-secondary)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--border-color)'
                        }}
                      >
                        {groupDragHandle(group)}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {group.name}
                            {group.is_active === false && (
                              <span className="badge badge-default" style={{ fontSize: '11px' }}>已禁用</span>
                            )}
                          </div>
                          {group.description && (
                            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                              {group.description}
                            </div>
                          )}
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                            {rules.filter(r => r.group_id === group.id).length} 条规则
                            {group.stop_on_match === false ? ' · 继续匹配' : ' · 匹配后停止'}
                            {group.delay_enabled ? ` · ${getDelayPolicyLabel(group)}` : ' · 未开启延迟'}
                            {group.webhook_url && (
                              <span style={{ marginLeft: '8px' }}>
                                · Webhook: <code style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: '3px' }}>{group.webhook_url}</code>
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openGroupEditorModal(group)}
                          >
                            编辑
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => deleteGroup(group.id)}
                          >
                            删除
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => openGroupEditorModal()}>
                新建规则组
              </button>
              <button className="btn btn-ghost" onClick={closeGroupListModal}>
                关闭
              </button>
            </div>

        </Modal>
      )}

      {/* 规则组编辑弹窗 */}
      {showGroupEditorModal && (
        <Modal isOpen title={<>{editingGroup ? '编辑规则组' : '新建规则组'}</>} onClose={closeGroupEditorModal} busy={groupSaving} size="large" className="admin-standard-dialog">


            <div className="modal-body">
              <div>
                <div className="form-group">
                  <label className="form-label">规则组名称 *</label>
                  <input
                    type="text"
                    value={groupForm.name}
                    onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
                    className="form-input"
                    placeholder="输入规则组名称"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">描述</label>
                  <input
                    type="text"
                    value={groupForm.description}
                    onChange={(e) => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
                    className="form-input"
                    placeholder="输入规则组描述（可选）"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Webhook URL</label>
                  <input
                    type="text"
                    value={groupForm.webhook_url}
                    onChange={(e) => setGroupForm(prev => ({ ...prev, webhook_url: e.target.value }))}
                    className="form-input"
                    placeholder="如：/webhook/group-1-xxx（可选，留空将自动生成）"
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    用于接收 Jira Webhook 的 URL 路径，留空系统将自动生成
                  </small>
                </div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={groupForm.is_active}
                      onChange={(e) => setGroupForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                    <span>启用规则组</span>
                  </label>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={groupForm.stop_on_match}
                      onChange={(e) => setGroupForm(prev => ({ ...prev, stop_on_match: e.target.checked }))}
                    />
                    <span>匹配成功后停止执行同组其他规则</span>
                  </label>
                </div>
                <div style={{ borderTop: '1px solid var(--border-color)', marginTop: '20px', paddingTop: '20px' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    非工作时间延迟
                  </h4>
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={delayPolicyForm.enabled}
                        onChange={(e) => setDelayPolicyForm(prev => ({ ...prev, enabled: e.target.checked }))}
                      />
                      <span>启用非工作时间延迟触发</span>
                    </label>
                  </div>
                  <div className="admin-dialog-form-grid">
                    <div className="form-group">
                      <label className="form-label">工作日历</label>
                      <SearchableSelect
                        value={delayPolicyForm.calendar_id}
                        options={[
                          { value: '', label: '选择工作日历' },
                          ...calendars.map(calendar => ({ value: String(calendar.id), label: calendar.name }))
                        ]}
                        onChange={(value) => setDelayPolicyForm(prev => ({ ...prev, calendar_id: value }))}
                        placeholder="选择工作日历"
                        disabled={!delayPolicyForm.enabled}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">判断字段</label>
                      <SearchableSelect
                        value={delayPolicyForm.condition_field}
                        options={delayFieldOptions}
                        onChange={(value) => setDelayPolicyForm(prev => ({ ...prev, condition_field: value }))}
                        placeholder="选择字段"
                        disabled={!delayPolicyForm.enabled}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">判断方式</label>
                      <SearchableSelect
                        value={delayPolicyForm.condition_operator}
                        options={delayOperatorOptions}
                        onChange={(value) => setDelayPolicyForm(prev => ({
                          ...prev,
                          condition_operator: value,
                          condition_value_text: ['is_empty', 'is_not_empty'].includes(value) ? '' : prev.condition_value_text
                        }))}
                        placeholder="选择判断方式"
                        searchable={false}
                        disabled={!delayPolicyForm.enabled}
                      />
                    </div>
                    {delayConditionNeedsValue && (
                      <div className="form-group">
                        <label className="form-label">判断值</label>
                        <input
                          type="text"
                          className="form-input"
                          value={delayPolicyForm.condition_value_text}
                          onChange={(e) => setDelayPolicyForm(prev => ({ ...prev, condition_value_text: e.target.value }))}
                          placeholder="多个值用英文逗号分隔"
                          disabled={!delayPolicyForm.enabled}
                        />
                      </div>
                    )}
                  </div>
                  <small style={{ color: 'var(--text-muted)' }}>
                    当前策略会在非工作时间且字段条件满足时，将规则组执行延迟到下一个工作时间开始；字段和判断方式与规则条件保持一致。
                  </small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {editingGroup && (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditingGroup(null);
                    setGroupForm({ name: '', description: '', is_active: true, stop_on_match: true, webhook_url: '' });
                    setDelayPolicyForm(defaultDelayPolicyForm);
                  }}
                >
                  取消编辑
                </button>
              )}
              <button className="btn btn-primary" onClick={saveGroup} disabled={groupSaving}>
                {groupSaving ? '保存中…' : editingGroup ? '保存修改' : '创建规则组'}
              </button>
              <button className="btn btn-ghost" onClick={closeGroupEditorModal} disabled={groupSaving}>
                关闭
              </button>
            </div>

        </Modal>
      )}

      <Confirm
        isOpen={confirmState.isOpen}
        title="确认操作"
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={() => setConfirmState({ ...confirmState, isOpen: false })}
      />

      <Alert
        isOpen={alertState.isOpen}
        title="提示"
        message={alertState.message}
        onClose={() => setAlertState({ ...alertState, isOpen: false })}
      />
    </div>
  );
};

export default RulesPage;
